import React, { useState } from 'react';
import { Search, FileText, ArrowRight, Loader2 } from 'lucide-react';
import api from '../../apiService';

function storageKey(userId?: string): string {
    return userId ? `bark_onboarding_${userId}` : 'bark_onboarding_complete';
}

function stepStorageKey(userId?: string): string {
    return userId ? `bark_onboarding_step_${userId}` : 'bark_onboarding_step';
}

function savedStep(userId?: string): number {
    try {
        const value = Number(localStorage.getItem(stepStorageKey(userId)));
        return Number.isInteger(value) && value >= 0 && value < 3 ? value : 0;
    } catch {
        return 0;
    }
}

function persistStep(step: number, userId?: string): void {
    try {
        localStorage.setItem(stepStorageKey(userId), String(step));
    } catch {
        // localStorage is an optional resume optimization.
    }
}

/** Fast local check — avoids flicker on page load. Server is authoritative. */
export function hasCompletedOnboarding(userId?: string): boolean {
    try {
        return localStorage.getItem(storageKey(userId)) === 'true';
    } catch {
        return false;
    }
}

/** Check server-side onboarding status and sync to localStorage. */
export async function checkOnboardingStatus(authToken?: string, userId?: string): Promise<boolean> {
    // Fast path: already marked locally
    if (hasCompletedOnboarding(userId)) return true;
    // If no token, fall back to local-only
    if (!authToken) return false;
    const { completed } = await api.getOnboardingStatus(authToken);
    if (completed) {
        try { localStorage.setItem(storageKey(userId), 'true'); } catch { /* noop */ }
    }
    return completed;
}

/** Mark onboarding as complete — persists both locally and server-side. */
async function completeOnboarding(authToken?: string, userId?: string): Promise<void> {
    if (authToken) {
        const { completed } = await api.completeOnboarding(authToken);
        if (!completed) {
            throw new Error('Could not save onboarding progress');
        }
    }

    try {
        localStorage.setItem(storageKey(userId), 'true');
        localStorage.removeItem(stepStorageKey(userId));
    } catch {
        // localStorage not available
    }
}

interface OnboardingFlowProps {
    onComplete: () => void;
    authToken?: string;
    userId?: string;
}

export default function OnboardingFlow({ onComplete, authToken, userId }: OnboardingFlowProps) {
    const [step, setStep] = useState(() => savedStep(userId));
    const [saving, setSaving] = useState<'complete' | 'skip' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [retryAction, setRetryAction] = useState<'complete' | 'skip'>('complete');
    const totalSteps = 3;
    const stepNames = ['Welcome to BARK', 'Meet your research assistant', 'Research, then build'];

    const selectStep = (nextStep: number) => {
        setError(null);
        setStep(nextStep);
        persistStep(nextStep, userId);
    };

    const finish = async (action: 'complete' | 'skip') => {
        setError(null);
        setRetryAction(action);
        setSaving(action);
        try {
            await completeOnboarding(authToken, userId);
            onComplete();
        } catch (caughtError) {
            setError(caughtError instanceof Error ? caughtError.message : 'Could not save onboarding progress');
        } finally {
            setSaving(null);
        }
    };

    const handleNext = () => {
        if (step < totalSteps - 1) {
            selectStep(step + 1);
        } else {
            void finish('complete');
        }
    };

    return (
        <section
            aria-label="BARK onboarding"
            className="fixed inset-0 z-[9999] bg-surface flex flex-col items-center justify-center"
        >
            <button
                type="button"
                onClick={() => void finish('skip')}
                disabled={saving !== null}
                aria-label={saving === 'skip' ? 'Skipping onboarding' : 'Skip tour'}
                className="absolute right-6 top-6 min-h-8 px-3 inline-flex items-center gap-2 rounded-lg text-sm font-medium text-fg-secondary hover:text-fg hover:bg-bg-warm disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
                {saving === 'skip' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Skip tour
            </button>

            {/* Content */}
            <div className="flex-1 flex items-center justify-center w-full max-w-2xl px-8">
                {step === 0 && <Step1Welcome onNext={handleNext} />}
                {step === 1 && <Step2Assistant onNext={handleNext} />}
                {step === 2 && <Step3Workflow onNext={handleNext} saving={saving === 'complete'} />}
            </div>

            {error && (
                <div
                    role="alert"
                    className="mb-4 mx-6 max-w-lg rounded-xl border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger"
                >
                    <p>{error}</p>
                    <button
                        type="button"
                        onClick={() => void finish(retryAction)}
                        disabled={saving !== null}
                        className="mt-2 min-h-8 rounded-lg px-3 font-medium underline underline-offset-2 hover:no-underline disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        Try again
                    </button>
                </div>
            )}

            {/* Dot indicators */}
            <div className="pb-10 flex items-center gap-2">
                {Array.from({ length: totalSteps }).map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => selectStep(i)}
                        disabled={saving !== null}
                        aria-current={i === step ? 'step' : undefined}
                        aria-label={`Go to onboarding step ${i + 1} of ${totalSteps}: ${stepNames[i]}`}
                        className="group min-w-8 min-h-8 inline-flex items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                        <span
                            aria-hidden="true"
                            className={`rounded-full transition-all duration-300 ${
                                i === step
                                    ? 'w-2.5 h-2.5 bg-primary'
                                    : 'w-2 h-2 bg-border-default group-hover:bg-fg-tertiary'
                            }`}
                        />
                    </button>
                ))}
            </div>
        </section>
    );
}

/** Step 1: Welcome to BARK */
function Step1Welcome({ onNext }: { onNext: () => void }) {
    return (
        <div className="text-center">
            {/* BARK icon */}
            <div className="flex justify-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                    </svg>
                </div>
            </div>

            <h1 className="text-3xl font-bold text-fg mb-3">
                Welcome to BARK
            </h1>
            <p className="text-fg-tertiary text-base mb-8 max-w-md mx-auto">
                BARK is your AI-powered regulatory research and optimization platform.
            </p>

            <button
                onClick={onNext}
                className="px-10 py-3 bg-primary hover:bg-primary-hover text-on-primary text-sm font-medium rounded-xl transition-colors shadow-sm"
            >
                Get started
            </button>
        </div>
    );
}

/** Step 2: Meet your research assistant */
function Step2Assistant({ onNext }: { onNext: () => void }) {
    const examples = [
        "What are the CET1 capital requirements for a Nordic bank?",
        "Explain the risk weight for residential mortgages under CRR",
        "Compare Swedish and Norwegian capital buffer requirements",
    ];

    return (
        <div className="text-center w-full">
            <h1 className="text-3xl font-bold text-fg mb-3">
                Meet your research assistant
            </h1>
            <p className="text-fg-tertiary text-base mb-8 max-w-lg mx-auto">
                Ask any regulatory question. BARK will research, analyze, and build reports for you.
            </p>

            {/* Example questions card */}
            <div className="bg-bg-warm rounded-2xl p-6 max-w-lg mx-auto mb-8">
                <p className="text-sm font-semibold text-fg-secondary mb-4">Try asking BARK:</p>
                <div className="space-y-2.5">
                    {examples.map((q, i) => (
                        <div
                            key={i}
                            className="bg-surface border border-border-default rounded-xl px-4 py-3 text-sm text-fg-secondary text-left"
                        >
                            "{q}"
                        </div>
                    ))}
                </div>
            </div>

            <button
                onClick={onNext}
                className="min-h-8 inline-flex items-center rounded-lg px-2 text-sm font-medium text-fg-secondary hover:text-fg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
                Continue
            </button>
        </div>
    );
}

/** Step 3: Research, then build */
function Step3Workflow({ onNext, saving }: { onNext: () => void; saving: boolean }) {
    return (
        <div className="text-center w-full">
            <h1 className="text-3xl font-bold text-fg mb-3">
                Research, then build
            </h1>
            <p className="text-fg-tertiary text-base mb-8 max-w-lg mx-auto">
                Once you've researched a topic, go ahead and build models, insert your data, and get optimal outcomes for your specific situation.
            </p>

            {/* Two cards with arrow */}
            <div className="flex items-center justify-center gap-4 max-w-2xl mx-auto mb-8">
                {/* Research card */}
                <div className="flex-1 bg-surface border border-border-default rounded-2xl p-6 text-left">
                    <div className="w-10 h-10 rounded-xl bg-bg-warm flex items-center justify-center mb-4">
                        <Search className="w-5 h-5 text-fg-secondary" />
                    </div>
                    <h3 className="text-sm font-bold text-fg mb-2">1. Regulatory research</h3>
                    <p className="text-xs text-fg-tertiary leading-relaxed">
                        Ask your question. BARK pulls the relevant regulations, guidelines, and supervisory expectations into one workspace.
                    </p>
                </div>

                {/* Arrow */}
                <ArrowRight className="w-5 h-5 text-fg-secondary flex-shrink-0" />

                {/* Build card */}
                <div className="flex-1 bg-bg-warm border border-border-default rounded-2xl p-6 text-left">
                    <div className="w-10 h-10 rounded-xl bg-bg-warm flex items-center justify-center mb-4">
                        <FileText className="w-5 h-5 text-fg-secondary" />
                    </div>
                    <h3 className="text-sm font-bold text-fg mb-2">2. Build & artifact</h3>
                    <p className="text-xs text-fg-tertiary leading-relaxed">
                        Build functional financial models, compliance memos, and audit-ready documents — pre-filled with your data.
                    </p>
                </div>
            </div>

            <button
                onClick={onNext}
                disabled={saving}
                aria-label={saving ? 'Saving onboarding' : 'Start using BARK'}
                className="min-h-8 inline-flex items-center gap-2 text-sm font-medium text-fg-secondary hover:text-fg transition-colors disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg px-2"
            >
                {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Start using BARK
            </button>
        </div>
    );
}
